import {CmsDocumentContentStatus, CmsDocumentContentType, Content} from "@/store/model/Content";
import {AttachmentName} from "@/store/model/Attachment";
import {Proposal, ProposalType} from "@/store/model/Proposal";
import {ProposalUpdate} from "@/store/model/ProposalUpdate";
import {VersionedEntityStatus} from "@/store/model/VersionedEntityStatus";
import {ProposalCreate} from "@/store/model/ProposalCreate";
import {CreatorType} from "@/store/model/ImgLabel";
import {setUrlParam} from "@/baseUtils";
import router from "@/router";
import axios from "axios";
import {defineStore, mapActions} from "pinia";
import {documentStore} from "@/store/documentStore";
import {argumentStore} from "@/store/argumentStore";
import {proposalStore} from "@/store/proposalStore";
import {baseLoaderStore} from "@/store/baseLoaderStore";
import {authStore} from "@/store/authStore";

export const editProposalStore = defineStore('editProposalStore', {
    state: () => ({
        autoSaveTimeout: null,
        autoSaveSavingStatus: 'SAVED',
        manuallySaveSavingState: false,
        autoSaveDelay: 3000,

        editProposalViewState: false,
        editedProposalData: null,

        //Edit proposal
        proposalReadyForSave: [],
        proposalTitleHasChanged: false,
        actualProposalHasChanged: false,
        proposalImportanceChanged: false,
        proposalArgumentsChanged: false,
        proposalNeedUpdateModalState: false,
        proposalCantSavingModalState: false,
        actualExamplesModalState: false,
        almostPublishModalState: false,
        savedDraftModalState: false,

        //Actual proposal
        selectedActualType: { name: 'Add text', value: 'HTML_TEXT' },
        actualProposalTextVal: '',
        actualProposalFile: null,

        //Proposal arguments
        editedProposalArguments: []
    }),
    getters: {
        editedProposalLoaded() {
            return this.editedProposalData
                && this.editedProposalData.status === VersionedEntityStatus.DRAFT;
        },
        isImportedProposal() {
            return this.editedProposalData.type === ProposalType.RIKSDAGEN_PROPOSAL;
        },
        editProposalHasChanges() {
            return true;

            // return this.proposalTitleHasChanged
            //     || this.actualProposalHasChanged
            //     || this.proposalImportancyChanged
            //     || this.proposalArgumentsChanged;
        },
        isDraftLoaded() {
            return this.editedProposalData && this.editedProposalData.status === VersionedEntityStatus.DRAFT;
        },
        notReviewedContent() {
            const proposalInfo = proposalStore().proposalInfo;
            return proposalInfo ? !proposalInfo.actualProposal && proposalInfo.type === 'RIKSDAGEN_PROPOSAL' : false;
        },
        autoSavingInProgress() {
            return this.autoSaveSavingStatus === 'SAVING';
        },
        publishButtonState() {
            return this.autoSavingInProgress || this.notReviewedContent;
        },
        draftProposalVersionId() {
            const draftProposalVersion = this.editedProposalData.versions.find((v) => v.status === VersionedEntityStatus.DRAFT);
            return draftProposalVersion ? draftProposalVersion.id : null;
        },
        latestProposalVersionId() {
            const latestProposalVersion = this.editedProposalData.versions.find((v) => v.status === VersionedEntityStatus.CURRENT);
            return latestProposalVersion ? latestProposalVersion.id : null;
        }
    },
    actions: {
        ...mapActions(baseLoaderStore, [
            'showBaseLoader',
            'hideBaseLoader'
        ]),
        ...mapActions(documentStore, [
            'createTextDocument',
            'updateTextDocument',
            'createAttachmentDocument',
            'updateAttachmentDocument'
        ]),
        ...mapActions(argumentStore, [
            'createNewArgument',
            'setArgumentContent',
            'getArguments',
            'updateArgument'
        ]),
        toggleEditProposalState() {
            this.editProposalViewState = !this.editProposalViewState;
        },
        closeEditProposalView() {
            this.editProposalViewState = false;
        },
        toggleActualExamplesModal() {
            this.actualExamplesModalState = !this.actualExamplesModalState;
        },
        setEditPropValue(prop, value) {
            this.editedProposalData[prop] = value;
        },
        setAsModified(entityName, state) {
            this[entityName] = state;
        },
        resetStoreData() {
            this.editedProposalData = null;
            this.editedProposalArguments = [];
            this.proposalTitleHasChanged = false;
            this.actualProposalHasChanged = false;
            this.proposalImportancyChanged = false;
            this.proposalArgumentsChanged = false;
            this.autoSaveSavingStatus = 'SAVED';
            this.manuallySaveSavingState = false;
        },
        getEditProposalInfo(proposalId) {
            let draftProposalVersion = null;

            return proposalStore().getProposalInfoAction(proposalId).then((resp)=> {
                this.editedProposalData = new Proposal(resp.data);
                if (!this.isDraftLoaded) {
                    draftProposalVersion = this.editedProposalData.versions.find((v) => v.status === VersionedEntityStatus.DRAFT);
                    if (!draftProposalVersion) {
                        return this.createProposalVersionAction();
                    } else {
                        return proposalStore().getProposalInfoAction(draftProposalVersion.id).then((resp)=> {
                            this.editedProposalData = new Proposal(resp.data);
                        });
                    }
                }
            });
        },

        // Actual proposal
        setActualProposalType(type) {
            this.selectedActualType = type;
        },
        setActualProposalText(text) {
            this.actualProposalTextVal = text;
        },
        setActualProposalFile(file) {
            this.actualProposalFile = file;
        },
        setUpdateActualProposalAction(proposalId, contentId) {
            return axios.put(`/api/proposals/${proposalId}/actual?contentId=${contentId}`);
        },
        setUpdateTextActualProposal(contentId) {
            const status = this.editedProposalData.actualProposal ? this.editedProposalData.actualProposal.status : null;
            const title = this.editedProposalData ? this.editedProposalData.title : "Actual proposal text";
            if (contentId && status === CmsDocumentContentStatus.DRAFT) {
                return  this.updateTextDocument(contentId, {
                    mimeType: "text/html",
                    text: this.actualProposalTextVal,
                    title: title,
                });
            } else {
                const contentNumber = this.editedProposalData.actualProposal ? this.editedProposalData.actualProposal.contentNumber : null;
                return this.createTextDocument({
                    mimeType: "text/html",
                    text: this.actualProposalTextVal,
                    title: title,
                }, contentNumber).then((resp)=> {
                    return this.setUpdateActualProposalAction(this.editedProposalData.id, resp.data.id)
                        .then((res)=> {
                            this.editedProposalData = new Proposal(res.data);
                        });
                });
            }
        },
        setUpdatePdfActualProposal(contentId) {
            const status = this.editedProposalData.actualProposal ? this.editedProposalData.actualProposal.status : null;
            if (contentId && status === CmsDocumentContentStatus.DRAFT) {
                const attachmentId = this.editedProposalData.actualProposal.attachments.find(a => a.name === AttachmentName.PDF_FILE).id;
                return this.updateAttachmentDocument(contentId, attachmentId, this.actualProposalFile)
                    .then((resp)=> {
                        this.setActualProposalFile(resp.data.attachments.find(a => a.name === AttachmentName.PDF_FILE));
                        this.editedProposalData.actualProposal = new Content(resp.data);
                        this.editedProposalData.actualProposal = new Content(resp.data);
                    });
            } else {
                const title = this.editedProposalData ? this.editedProposalData.title : "Actual proposal file";
                const contentNumber = this.editedProposalData.actualProposal ? this.editedProposalData.actualProposal.contentNumber : null;
                return this.createAttachmentDocument(this.actualProposalFile, CmsDocumentContentType.PDF, title, contentNumber)
                    .then((resp)=> {
                        this.setUpdateActualProposalAction(this.editedProposalData.id, resp.data.id)
                            .then((res)=> {
                                this.editedProposalData = new Proposal(res.data);
                            });
                    });
            }
        },
        processActualProposalSaving() {
            const contentId = this.editedProposalData.actualProposal ? this.editedProposalData.actualProposal.contentId : null;

            if (this.actualProposalHasChanged) {
                if (this.selectedActualType.value === 'HTML_TEXT') {
                    if (this.actualProposalTextVal) {
                        return this.setUpdateTextActualProposal(contentId);
                    }
                } else if (this.selectedActualType.value === 'PDF') {
                    if (this.actualProposalFile) {
                        return this.setUpdatePdfActualProposal(contentId);
                    }
                }
            } else {
                return Promise.resolve();
            }
        },

        // Arguments
        addProposalArgument(argData) {
            this.editedProposalArguments.push(argData);
        },
        addNewArgument(ignoreChanges) {
            this.addProposalArgument({
                title: '',
                creatorType: 'USER',
                proposalId: this.editedProposalData.id,
                creatorId: this.editedProposalData.proposer.id,
                type: 'VIDEO',
                order: this.editedProposalArguments.length + 1
            })

            if (!ignoreChanges) {
                this.setAsModified('proposalArgumentsChanged', true);
                this.initAutoSave('ARGUMENT');
            }
        },
        setEditedProposalArguments(argumentsList) {
            this.editedProposalArguments = argumentsList;
        },
        removeProposalArgument(argIndex) {
            this.editedProposalArguments = this.editedProposalArguments.filter((item, index)=> {
                return index !== argIndex;
            })
        },
        async processArgumentsSaving() {
            let readyForSave = [];

            this.editedProposalArguments.forEach((arg, index, argsList) => {
                const argHasContent = !!arg.argumentDetailed;
                const argIsDraft = arg.status === 'DRAFT';

                if (!arg.changedTitle && !arg.changedContent || arg.title.length < 5) {
                    return false;
                }

                let argData = {
                    "order": arg.order,
                    "title": arg.title,
                    "argumentNumber": arg.argumentNumber
                };

                if (argIsDraft) {
                    readyForSave.push(this.updateArgument(arg.id, argData).then((argResp)=> {
                        argsList[index] = argResp.data;

                        if (arg.type === 'TEXT') {
                            if (argHasContent && arg.argumentDetailed.status === CmsDocumentContentStatus.DRAFT) {
                                return this.updateTextDocument(argResp.data.argumentDetailed.contentId, {
                                    mimeType: "text/html",
                                    text: arg.argText,
                                    title: arg.title,
                                });
                            } else {
                                const contentNumber = arg.argumentDetailed ? arg.argumentDetailed.contentNumber : null;
                                return this.createTextDocument({
                                    mimeType: "text/html",
                                    text: arg.argText,
                                    title: arg.title,
                                }, contentNumber).then((docResp)=> {
                                    return this.setArgumentContent(argResp.data.id, docResp.data.id);
                                });
                            }
                        } else if (arg.type === 'VIDEO') {
                            return this.setArgumentContent(argResp.data.id, arg.attachedVideoDoc.id);
                        }
                    }));
                } else {
                    if (!argData.argumentNumber) {
                        argData.proposalId = this.editedProposalData.id;
                    }

                    readyForSave.push(this.createNewArgument(argData).then((argResp)=> {
                        argsList[index] = argResp.data;

                        if (!argData.argumentNumber) {
                            if (arg.type === 'TEXT') {
                                return this.createTextDocument({
                                    mimeType: "text/html",
                                    text: arg.argText,
                                    title: arg.title,
                                }).then((docResp)=> {
                                    return this.setArgumentContent(argResp.data.id, docResp.data.id);
                                })
                            } else if (arg.type === 'VIDEO') {
                                if (arg.attachedVideoDoc) {
                                    return this.setArgumentContent(argResp.data.id, arg.attachedVideoDoc.id);
                                }
                            }
                        }
                    }))
                }
            });

            return await Promise.all(readyForSave);
        },

        //Proposal
        previewProposal() {
            this.updateProposal(true).then(()=> {
                this.openPreviewProposal();
            });
        },
        openPreviewProposal() {
            const draft = this.editedProposalData.versions.find(p => p.status === VersionedEntityStatus.DRAFT);
            this.closeEditProposalView();
            this.hideBaseLoader();
            router.push({ name: 'ProposalView', params: { id: (draft ? draft.id : this.editedProposalData.id) } });
        },
        saveProposal() {
            this.updateProposal(true).then(()=> {
                this.successfullySaveProposal();
                this.toggleSavedDraftModalState();
            });
        },
        publishProposal() {
            return this.updateProposal(this.editProposalHasChanges).then(()=> {
                return this.publishProposalAction().then(()=> {
                        this.successfullySaveProposal();
                        this.hideBaseLoader();
                    })
                    .catch((error)=> {
                        const statusCode = error.status;
                        if (statusCode === 409) {
                            this.hideBaseLoader();
                            this.toggleProposalNeedUpdateModalState();
                        }
                    })
            })
        },
        async processProposalSaving(actionType) {
            if (this.isDraftLoaded) {
                this.showBaseLoader();
                this.proposalReadyForSave.push(await this.processActualProposalSaving());
                this.proposalReadyForSave.push(await this.processArgumentsSaving());
                await Promise.all(this.proposalReadyForSave).then(()=> {
                    switch (actionType)
                    {
                        case 'PREVIEW':
                            this.previewProposal();
                            break;
                        case 'DRAFT':
                            this.saveProposal();
                            break;
                        case 'PUBLISH':
                            this.publishProposal();
                            break;
                    }
                });
            } else {
                throw new Error("Illegal edited proposal state: " + this.editedProposalData.status);
            }
        },
        successfullySaveProposal() {
            const current = this.editedProposalData.versions.find(p => p.status === VersionedEntityStatus.CURRENT);

            router.push({ name: 'ProposalView', params: { id: (current ? current.id : this.editedProposalData.id) } });
            this.resetStoreData();
            this.closeEditProposalView();
            proposalStore().closeCreatePropFormView();
            this.setManuallySaveSavingState(false);
            setUrlParam('publish', false);
            this.hideBaseLoader();

            if (current) {
                proposalStore().getProposalInfo(current.id);
            }
        },

        initAutoSave(type) {
            if (this.autoSaveSavingStatus === 'ERROR') {
                this.toggleProposalCantSavingModalState();
                return;
            }

            if (this.autoSaveTimeout) {
                clearTimeout(this.autoSaveTimeout);
                this.autoSaveTimeout = null;
            }

            this.autoSaveTimeout = setTimeout(()=> {
                this.autoSaveSavingStatus = 'SAVING';

                if (router.currentRoute._value.name !== 'EditProfileView'
                    && router.currentRoute._value.name !== 'ProposalCreateView'
                ) {
                    this.autoSaveSavingStatus = 'SAVED';
                    this.proposalReadyForSave = [];
                    return;
                }

                if (this.manuallySaveSavingState) {
                    return;
                }

                if (type === 'PROPOSAL') {
                    this.proposalReadyForSave.push(this.updateProposal().then(()=> {
                        this.setAsModified('proposalTitleHasChanged', false);
                        this.autoSaveSavingStatus = 'SAVED';
                    }).catch(()=> {
                        this.autoSaveSavingStatus = 'ERROR';
                    }))
                } else if (type === 'ACTUAL_PROPOSAL') {
                    this.proposalReadyForSave.push(this.processActualProposalSaving().then(()=> {
                        this.setAsModified('actualProposalHasChanged', false);
                        this.autoSaveSavingStatus = 'SAVED';
                    }));
                } else if (type === 'ARGUMENT') {
                    this.proposalReadyForSave.push(this.processArgumentsSaving().then(()=> {
                        this.setAsModified('proposalArgumentsChanged', false);
                        this.autoSaveSavingStatus = 'SAVED';
                    }))
                }
            }, this.autoSaveDelay);
        },

        updateProposal() {
            const proposalId = this.editedProposalData.id;
            const data = new ProposalUpdate(
                this.editedProposalData.title,
                this.editedProposalData.getImportanceTypeOr(),
                this.editedProposalData.changeType,
                null
            );

            return this.updateProposalAction(proposalId, data).then((resp)=> {
                this.editedProposalData = new Proposal(resp.data);
            });
        },
        updateProposalAction(proposalId, data) {
            return axios.put(`/api/proposals/${proposalId}`, data);
        },
        async createProposalVersionAction() {
            const data = new ProposalCreate(
                this.editedProposalData.title,
                this.isImportedProposal ? authStore().userInfo.id : this.editedProposalData.getProposerIdOr(),
                this.isImportedProposal ? CreatorType.USER : this.editedProposalData.getCreatorTypeOr(),
                this.editedProposalData.getImportanceTypeOr(),
                this.editedProposalData.issueNumber
            );

            return axios.post(`/api/proposals`, data).then((res) => {
                this.editedProposalData = new Proposal(res.data);
            });
        },

        async createNewProposalAction(title, proposerId, creatorType, importanceType) {
            const data = new ProposalCreate(
                title,
                proposerId,
                creatorType,
                importanceType,
                null
            );
            return axios.post(`/api/proposals`, data).then((res) => {
                this.editedProposalData = new Proposal(res.data);
            });
        },
        publishProposalAction() {
            const proposalId = this.editedProposalData.id;

            return axios.put(`/api/proposals/${proposalId}/publish`).then((res) => {
                this.editedProposalData = new Proposal(res.data);
            });
        },
        setManuallySaveSavingState(state) {
           this.manuallySaveSavingState = state;
        },
        toggleAlmostPublishModal() {
            this.almostPublishModalState = !this.almostPublishModalState;
        },
        toggleSavedDraftModalState() {
            this.savedDraftModalState = !this.savedDraftModalState;
        },
        toggleProposalNeedUpdateModalState() {
            this.proposalNeedUpdateModalState = !this.proposalNeedUpdateModalState;
        },
        toggleProposalCantSavingModalState() {
            this.proposalCantSavingModalState = !this.proposalCantSavingModalState;
        },
        openPublishedVersion() {
            const proposalId = this.latestProposalVersionId || this.draftProposalVersionId;
            this.editProposalViewState = false;
            router.push({ name: 'ProposalView', params: { id: proposalId } });
        }
    }
});